import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import moment from "moment";
import { getPmSessionMonthRangeStrings } from "helpers/pmSessionDateHelper";
import { useCptFilterLoading } from "../common/useCptFilterLoading";
import "./css/IntegrationFilter.css";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" }
];

const YEARS = Array.from({ length: 5 }, (_, i) => (moment().year() - i).toString());

const buildDateRange = (month, year) => {
  const { startDate, endDate } = getPmSessionMonthRangeStrings(year, month);
  return {
    startDate,
    endDate,
    selectedMonth: month,
    selectedYear: year
  };
};

const IntegrationFilter = ({
  selectedMonth: propSelectedMonth,
  selectedYear: propSelectedYear,
  currentMonth: propCurrentMonth,
  onFilterChange,
  onApply,
  loading = false,
  title = "CPT Code Performance",
  subTitle = "Select month and year to filter CPT code performance data"
}) => {
  const { isFilterBusy, beginSubmit } = useCptFilterLoading(loading);
  const [selectedMonth, setSelectedMonth] = useState(
    propSelectedMonth || moment().format("MM")
  );
  const [selectedYear, setSelectedYear] = useState(
    propSelectedYear || moment().format("YYYY")
  );
  const [filterApplied, setFilterApplied] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setSelectedMonth(propSelectedMonth || moment().format("MM"));
    setSelectedYear(propSelectedYear || moment().format("YYYY"));
  }, [propSelectedMonth, propSelectedYear]);

  const getDateRange = () => buildDateRange(selectedMonth, selectedYear);

  const validateSelection = () => {
    if (!selectedMonth || selectedMonth === "") {
      return { isValid: false, message: "Please select a month" };
    }
    if (!selectedYear || selectedYear === "") {
      return { isValid: false, message: "Please select a year" };
    }

    const selectedDate = moment(`${selectedYear}-${selectedMonth}-01`);
    const currentDate = moment();

    if (selectedDate.isAfter(currentDate, "month")) {
      const selectedMonthLabel = MONTHS.find((m) => m.value === selectedMonth)?.label || "";
      return {
        isValid: false,
        message: `Data not available for ${selectedMonthLabel} ${selectedYear}. Future dates are not allowed.`
      };
    }

    return { isValid: true, message: "" };
  };

  const handleMonthChange = (e) => {
    const month = e.target.value;
    setSelectedMonth(month);
    setValidationMessage("");
    setIsValid(true);
    if (onFilterChange) {
      onFilterChange(buildDateRange(month, selectedYear));
    }
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    setValidationMessage("");
    setIsValid(true);
    if (onFilterChange) {
      onFilterChange(buildDateRange(selectedMonth, year));
    }
  };

  const handleApply = () => {
    const validation = validateSelection();

    if (!validation.isValid) {
      setValidationMessage(validation.message);
      setIsValid(false);
      setFilterApplied(false);
      return;
    }

    const dateRange = getDateRange();
    setFilterApplied(true);
    setValidationMessage("");
    setIsValid(true);
    beginSubmit();

    if (onApply) {
      onApply(dateRange);
    }
  };

  const handleReset = () => {
    const currentMonth = moment().format("MM");
    const currentYear = moment().format("YYYY");
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
    setFilterApplied(false);
    setValidationMessage("");
    setIsValid(true);
    const range = buildDateRange(currentMonth, currentYear);
    if (onFilterChange) {
      onFilterChange(range);
    }
    beginSubmit();
    if (onApply) {
      onApply(range);
    }
  };

  const selectedMonthLabel = MONTHS.find((m) => m.value === selectedMonth)?.label || "";
  const displayMonth = propCurrentMonth || `${selectedMonthLabel} ${selectedYear}`;

  return (
    <div className="cpt-filter-wrapper">
      <div className={`filter-card-modern ${isFilterBusy ? "filter-card-modern--loading" : ""}`}>
        <div className="filter-content-row">
          <div className="filter-inputs-section">
            <div className="filter-header-section">
              <div className="filter-title-section">
                <h4>
                  {title}
                </h4>
                <p>{subTitle}</p>
              </div>
            </div>
            <Row className="g-3 align-items-end">
              <Col md="4">
                <div className="filter-input-group">
                  <label className="filter-label">
                    <i className="mdi mdi-calendar-month"></i>
                    Month
                  </label>
                  <div className="input-wrapper-modern">
                    <select
                      className="filter-select-modern"
                      value={selectedMonth}
                      onChange={handleMonthChange}
                      disabled={isFilterBusy}
                    >
                      {MONTHS.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Col>
              <Col md="4">
                <div className="filter-input-group">
                  <label className="filter-label">
                    <i className="mdi mdi-calendar-year"></i>
                    Year
                  </label>
                  <div className="input-wrapper-modern">
                    <select
                      className="filter-select-modern"
                      value={selectedYear}
                      onChange={handleYearChange}
                      disabled={isFilterBusy}
                    >
                      {YEARS.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Col>
              <Col md="4">
                <div className="filter-actions-section">
                  <button
                    className={`filter-btn-apply-modern ${isFilterBusy ? "filter-btn-apply-modern--loading" : ""}`}
                    onClick={handleApply}
                    type="button"
                    aria-busy={isFilterBusy}
                  >
                    {isFilterBusy ? <i className="mdi mdi-loading mdi-spin" /> : <i className="mdi mdi-filter" />}
                    {isFilterBusy ? "Loading..." : "Apply Filter"}
                  </button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="selected-date-display">
            {validationMessage && (
              <div className={`filter-message ${isValid ? "success" : "error"}`}>
                <i className={`mdi ${isValid ? "mdi-check-circle" : "mdi-alert-circle"}`}></i>
                <span>{validationMessage}</span>
              </div>
            )}
            {filterApplied && !validationMessage && (
              <div className="selected-date-card">
                <div className="selected-date-icon">
                  <i className="mdi mdi-calendar-check"></i>
                </div>
                <div className="selected-date-info">
                  <span className="selected-date-label">{displayMonth}</span>
                  <span className="selected-date-value">
                    Data for {displayMonth}
                  </span>
                </div>
                <button className="clear-selection-btn" onClick={handleReset} type="button" disabled={isFilterBusy}>
                  <i className="mdi mdi-close-circle"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationFilter;
